import { useCallback, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';

import type { StpNotification } from '~/components/ClaimPage/CliveApp/CliveNotifications/types';
import {
  type ConductorStatusObservation,
  isRunWorking,
} from '~/components/ClaimPage/CliveApp/conductorStatusObservation';
import useDataFetcher from '~/components/useDataFetcher';
import type {
  ClaimConductorMessageResponse,
  ClaimConductorMessagesResponse,
  SendConductorMessageRequest,
} from '~/server_shared/openapi/data-contracts';
import { getNotifyUserMessage } from '~/Utils/errors/notifyUserMessage';

import type { QueuedMessage } from '../conversation/types';
import { isHandlerTurn } from '../utils';

import { useRefetchForNewEntries } from './useRefetchForNewEntries';

/** The queue until the first read lands, and where that read failed. */
const NO_MESSAGES: QueuedMessage[] = [];

export interface HandlerMessages {
  /**
   * The claim's open messages: empty until the first read lands, and where it failed. A failed re-read keeps the
   * list as it was.
   */
  queue: QueuedMessage[];
  /** Whether the first read has yet to settle, landed or failed. */
  isLoading: boolean;
  /** Sends a message to Clive and resolves with its id; rejects with the reason it did not go. */
  send: (text: string) => Promise<string>;
}

/**
 * The handler's open messages to Clive on the claim, and the send behind the composer.
 *
 * Read again after each send, and whenever a `handler_turn` entry lands, by `useRefetchForNewEntries`' rules:
 * a run publishes one once the stamp of the message it read has committed. A card's rejection note is a
 * `handler_turn` entry too, and costs a read though it moves no message. A run's publish is made at most once,
 * so the run's end is read for too: once each time the status stops saying a run works, the message a lost
 * entry should have announced shows as read.
 *
 * No error dialog for either. A failed first read leaves the queue empty, and a failed re-read leaves the list
 * as it was, under a composer that still sends. A send that fails rejects with the conductor's reason where it
 * refused the message, and with the request's own error otherwise, and the composer's box says it was not sent.
 */
export const useHandlerMessages = (
  claimId: number,
  notifications: StpNotification[],
  /** The stream has its first snapshot. */
  isStreamReady: boolean,
  /** Clive's status on the claim, as the conversation reads it. */
  status: ConductorStatusObservation | null
): HandlerMessages => {
  const { data, isLoading, reloadData } = useDataFetcher<ClaimConductorMessagesResponse>(
    `/api/v1/claims/${claimId}/conductor/messages?status=open`,
    null,
    { shouldOpenErrorDialog: false }
  );

  useRefetchForNewEntries(notifications, isHandlerTurn, !isLoading && isStreamReady, reloadData);

  // By whether a run works, not by the observation: a poll that reads idle again is a new object and owes nothing.
  const isWorking = isRunWorking(status);
  const wasWorking = useRef(isWorking);
  useEffect(() => {
    if (wasWorking.current && !isWorking) reloadData();
    wasWorking.current = isWorking;
  }, [isWorking, reloadData]);

  const send = useCallback(
    async (text: string): Promise<string> => {
      const { data: sent } = await axios
        .post<ClaimConductorMessageResponse>(`/api/v1/claims/${claimId}/conductor/messages`, {
          text,
        } satisfies SendConductorMessageRequest)
        .catch((error: unknown) => {
          throw new Error(getNotifyUserMessage(error) ?? (error instanceof Error ? error.message : ''));
        });
      await reloadData();
      return sent.message.id;
    },
    [claimId, reloadData]
  );

  const queue = data?.items ?? NO_MESSAGES;

  return useMemo(() => ({ queue, isLoading, send }), [queue, isLoading, send]);
};
